import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useCallback, useMemo, useState } from 'react'

import { AlertCircleIcon, ArrowLeftIcon } from '@/components/icons'
import { ContactCsFloatingButton } from '@/components/ui/ContactCsFloatingButton'
import { RouteSpinner } from '@/components/ui/RouteSpinner'
import { GroupDetailFabGroup } from '@/features/group/components/GroupDetailFabGroup'
import { AppToast } from '@/components/ui/Toast'
import { hasSenderDetail } from '@/features/group/address'
import { AddOrderPanel } from '@/features/group/components/AddOrderPanel'
import { GroupDetailHeader } from '@/features/group/components/GroupDetailHeader'
import { InvoiceChargeSuccessHost } from '@/features/group/components/InvoiceChargeSuccessHost'
import { NeedToBuyBanner } from '@/features/group/components/NeedToBuyBanner'
import { OrderList } from '@/features/group/components/OrderList'
import { ProcessingOrderList } from '@/features/group/components/ProcessingOrderList'
import { OrderStatusTabs } from '@/features/group/components/OrderStatusTabs'
import { ShippingDetailPanel } from '@/features/group/components/ShippingDetailPanel'
import { ShippingDetailPromptModal } from '@/features/group/components/ShippingDetailPromptModal'
import {
  defaultDetailGroupSearch,
  detailGroupSearchSchema,
  type DetailGroupSearch,
} from '@/features/group/detail-group-search'
import {
  activeBadgeCountKey,
  buildBadgeCounts,
  emptyStateFor,
  filterFor,
  ORDER_PAGE_SIZE,
  shouldHideOrderTabCounts,
  type OrderTabKey,
} from '@/features/group/order-tabs'
import { readCachedGroupItem } from '@/features/group/cached-group-item'
import {
  groupAddressQueryOptions,
  groupDetailInfiniteQueryOptions,
} from '@/features/group/queries'
import { isCancelledError, isNotFound } from '@/lib/api/errors'
import { cn } from '@/lib/cn'
import { ensureQueryData } from '@/lib/query-client'
import { useDebouncedValue } from '@/lib/use-debounced-value'
import { useDocumentTitle } from '@/lib/useDocumentTitle'

const ORDER_LIST_ID = 'daftar-pesanan'

type ShippingPromptState = {
  entryDismissed: boolean
  manualOpen: boolean
}

function defaultShippingPromptState(): ShippingPromptState {
  return { entryDismissed: false, manualOpen: false }
}

function orderFilter(groupId: string, search: DetailGroupSearch) {
  return {
    groupId,
    ...filterFor(search.tab),
  }
}

/** Manage the orders inside one group (SCR-06), reached from "Kelola" on the dashboard. */
export const Route = createFileRoute('/_authed/detail-group/$id')({
  validateSearch: detailGroupSearchSchema,
  // `_authed` is already matched on Kelola navigation, so its pendingMs does
  // not apply — show RouteSpinner as soon as this route's loader is in flight.
  pendingMs: 0,
  pendingMinMs: 0,
  // Tab / search updates stay on this match — let React Query paint the list,
  // not a full-screen pending overlay.
  shouldReload: ({ cause }) => cause !== 'stay',
  loader: async ({ context, params, location }) => {
    const routeSearch = detailGroupSearchSchema.parse(location.search)
    const filter = orderFilter(params.id, routeSearch)

    await Promise.all([
      ensureQueryData(context.queryClient, groupAddressQueryOptions(params.id)).catch(() => null),
      context.queryClient
        .infiniteQuery(
          groupDetailInfiniteQueryOptions({
            ...filter,
            search: '',
            perPage: ORDER_PAGE_SIZE,
          }),
        )
        .catch((error: unknown) => {
          if (isNotFound(error)) return undefined
          if (isCancelledError(error)) return undefined
          throw error
        }),
    ])
  },
  component: DetailGroupPage,
  errorComponent: GroupUnavailable,
})

function DetailGroupPage() {
  const { id } = Route.useParams()
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  useDocumentTitle('Kelola Grup')

  const tab = search.tab
  const subTab = search.subTab

  const [orderSearch, setOrderSearch] = useState('')
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false)
  const [shippingOpen, setShippingOpen] = useState(false)
  const [promptByGroupId, setPromptByGroupId] = useState<Record<string, ShippingPromptState>>({})
  const promptState = promptByGroupId[id] ?? defaultShippingPromptState()
  const [addOrderOpen, setAddOrderOpen] = useState(false)
  const [createdToastOpen, setCreatedToastOpen] = useState(false)
  const [shippingSavedToastOpen, setShippingSavedToastOpen] = useState(false)
  const [processedToastOpen, setProcessedToastOpen] = useState(false)
  const debouncedSearch = useDebouncedValue(orderSearch)

  const queryClient = useQueryClient()
  const cachedGroup = readCachedGroupItem(queryClient, id)
  const filter = orderFilter(id, search)

  const infiniteDetailQuery = useInfiniteQuery({
    ...groupDetailInfiniteQueryOptions({
      ...filter,
      search: debouncedSearch,
      perPage: ORDER_PAGE_SIZE,
    }),
    retry: (count, error) => !isNotFound(error) && count < 2,
  })

  const ordersUnavailable = isNotFound(infiniteDetailQuery.error)

  const orders = useMemo(() => {
    return infiniteDetailQuery.data?.pages.flatMap((page) => page.orders) ?? []
  }, [infiniteDetailQuery.data])

  const detailMeta = infiniteDetailQuery.data?.pages[0]

  const group = detailMeta?.group ?? cachedGroup
  const badgeGroup = group ?? cachedGroup

  /** Open tab: list pagination. Other tabs: group-list counters. No fan-out on load. */
  const seededBadgeKey = activeBadgeCountKey(tab)
  const seededBadgeCount =
    infiniteDetailQuery.isSuccess ? detailMeta?.pagination.totalCount : undefined

  const counts = useMemo(
    () =>
      buildBadgeCounts({
        listed: badgeGroup,
        search: debouncedSearch,
        activeBadgeKey: seededBadgeKey,
        activeBadgeCount: seededBadgeCount,
        ordersUnavailable,
      }),
    [
      badgeGroup,
      debouncedSearch,
      seededBadgeKey,
      seededBadgeCount,
      ordersUnavailable,
    ],
  )

  const addressQuery = useQuery(groupAddressQueryOptions(id))
  const needsShippingSetup =
    !hasSenderDetail(addressQuery.data ?? null) && !group?.merchantAddress?.trim()
  const addressReady = addressQuery.isFetched
  const showEntryPrompt = Boolean(
    addressReady &&
      needsShippingSetup &&
      !promptState.entryDismissed &&
      group?.isVerified &&
      !ordersUnavailable,
  )
  const showShippingPrompt = showEntryPrompt || promptState.manualOpen

  function patchPromptState(patch: Partial<ShippingPromptState>) {
    setPromptByGroupId((current) => ({
      ...current,
      [id]: { ...(current[id] ?? defaultShippingPromptState()), ...patch },
    }))
  }

  function closeShippingPrompt() {
    patchPromptState({ manualOpen: false, entryDismissed: true })
  }

  function openShippingPanel() {
    setShippingOpen(true)
  }

  function openShippingFromPrompt() {
    patchPromptState({ manualOpen: false, entryDismissed: true })
    openShippingPanel()
  }

  function ensureShippingBeforeCharge() {
    if (!needsShippingSetup) return true
    patchPromptState({ manualOpen: true })
    return false
  }

  function patchSearch(patch: Partial<DetailGroupSearch>) {
    void navigate({
      search: (current) => ({ ...current, ...patch }),
    })
  }

  function selectTab(next: OrderTabKey) {
    patchSearch({ tab: next, subTab: 'all' })
  }

  const loadMore = useCallback(() => {
    if (!infiniteDetailQuery.hasNextPage) return
    if (infiniteDetailQuery.isFetchingNextPage) return
    void infiniteDetailQuery.fetchNextPage()
  }, [infiniteDetailQuery])

  const listPending = infiniteDetailQuery.isPending && orders.length === 0
  const listStale =
    infiniteDetailQuery.isFetching &&
    !infiniteDetailQuery.isFetchingNextPage &&
    orders.length > 0

  if (!group && infiniteDetailQuery.isPending) {
    return <RouteSpinner />
  }

  if (!group) {
    return <GroupUnavailable error={infiniteDetailQuery.error ?? new Error('Grup tidak ditemukan')} />
  }

  const shippingFilled = !needsShippingSetup

  const headerProps = {
    group,
    needToBuyCount: detailMeta?.needToBuyCount ?? 0,
    search: orderSearch,
    onSearchChange: setOrderSearch,
    shippingFilled,
    onOpenShipping: openShippingPanel,
    canAddOrder: group.isVerified && !ordersUnavailable,
    onAddOrder: () => setAddOrderOpen(true),
  }

  const orderListBody =
    tab === 'processing' ? (
      <ProcessingOrderList
        key={debouncedSearch}
        id={ORDER_LIST_ID}
        groupId={id}
        orders={orders}
        hasNextPage={Boolean(infiniteDetailQuery.hasNextPage)}
        isFetchingNextPage={infiniteDetailQuery.isFetchingNextPage}
        onLoadMore={loadMore}
        isPending={listPending && !ordersUnavailable}
        isError={infiniteDetailQuery.isError && !ordersUnavailable}
        onRetry={() => void infiniteDetailQuery.refetch()}
        isStale={listStale}
        search={debouncedSearch}
        emptyState={emptyStateFor(tab)}
        onPrinted={() => {
          patchSearch({ tab: 'shipped', subTab: 'all' })
          setProcessedToastOpen(true)
        }}
      />
    ) : (
      <OrderList
        id={ORDER_LIST_ID}
        groupId={id}
        originRouteId={group.merchantRouteId}
        tab={tab}
        subTab={subTab}
        orders={orders}
        hasNextPage={Boolean(infiniteDetailQuery.hasNextPage)}
        isFetchingNextPage={infiniteDetailQuery.isFetchingNextPage}
        onLoadMore={loadMore}
        isPending={listPending && !ordersUnavailable}
        isError={infiniteDetailQuery.isError && !ordersUnavailable}
        onRetry={() => void infiniteDetailQuery.refetch()}
        isStale={listStale}
        search={debouncedSearch}
        emptyState={emptyStateFor(tab)}
        onBeforeChargeOpen={ensureShippingBeforeCharge}
      />
    )

  return (
    <div className="min-h-dvh bg-slate-50">
      <div className="group-detail-page gap-0 lg:gap-4">
        {/* Mobile header */}
        <div className="bg-white px-4 py-4 lg:hidden">
          <GroupDetailHeader
            {...headerProps}
            searchOpen={mobileSearchOpen}
            onSearchOpenChange={setMobileSearchOpen}
            onRefresh={() => void infiniteDetailQuery.refetch()}
            isRefreshing={
              infiniteDetailQuery.isFetching && !infiniteDetailQuery.isFetchingNextPage
            }
          />
        </div>

        {/* Desktop header card */}
        <div className="hidden rounded-card bg-white px-4 py-3 ring-1 ring-slate-200 lg:block">
          <GroupDetailHeader
            {...headerProps}
            searchOpen={false}
            onSearchOpenChange={setMobileSearchOpen}
          />
        </div>

        {ordersUnavailable || !group.isVerified ? (
          <div className="px-4 lg:px-0">
            <UnverifiedBanner name={group.name} />
          </div>
        ) : null}

        {/* Tabs + orders — mobile: white column continuation; desktop: separate card */}
        <section className="bg-white px-4 pt-2 pb-5 lg:rounded-card lg:px-5 lg:py-4 lg:ring-1 lg:ring-slate-200">
          <OrderStatusTabs
            value={tab}
            counts={counts}
            onChange={selectTab}
            controls={ORDER_LIST_ID}
            hideCounts={shouldHideOrderTabCounts(orderSearch)}
          />

          {tab === 'unpaid' && !ordersUnavailable ? (
            <div className={cn(mobileSearchOpen && 'hidden lg:block')}>
              <NeedToBuyBanner count={detailMeta?.needToBuyCount ?? 0} />
            </div>
          ) : null}

          {orderListBody}
        </section>
      </div>

      <ShippingDetailPromptModal
        isOpen={showShippingPrompt}
        onClose={closeShippingPrompt}
        onSetAddress={openShippingFromPrompt}
      />

      {shippingOpen ? (
        <ShippingDetailPanel
          groupId={id}
          address={
            addressQuery.data ??
            (group.merchantAddress
              ? {
                  name: group.merchantName,
                  phone: null,
                  routeId: group.merchantRouteId,
                  addressLine: group.merchantAddress,
                  additionalAddress: null,
                  logoUrl: group.logoUrl,
                  lat: null,
                  lng: null,
                  phoneCodeId: null,
                  socialMediaUrls: [],
                }
              : null)
          }
          onClose={() => setShippingOpen(false)}
          onSaved={() => {
            setShippingOpen(false)
            setShippingSavedToastOpen(true)
          }}
        />
      ) : null}

      {addOrderOpen ? (
        <AddOrderPanel
          groupId={id}
          groupName={group.name}
          onClose={() => setAddOrderOpen(false)}
          onCreated={() => {
            setAddOrderOpen(false)
            patchSearch(defaultDetailGroupSearch)
            setCreatedToastOpen(true)
          }}
        />
      ) : null}

      <AppToast
        open={createdToastOpen}
        title="Pesanan ditambahkan"
        description="Pesanan manual sudah masuk di tab Belum bayar."
        variant="success"
        onClose={() => setCreatedToastOpen(false)}
      />

      <AppToast
        open={shippingSavedToastOpen}
        variant="success"
        title="Detail pengiriman berhasil disimpan"
        description=""
        onClose={() => setShippingSavedToastOpen(false)}
      />

      <InvoiceChargeSuccessHost />

      <AppToast
        open={processedToastOpen}
        title="Pesanan berhasil di proses"
        description="Label berhasil di download & drop paketmu ke Agen Lion Parcel manapun"
        onClose={() => setProcessedToastOpen(false)}
      />

      <GroupDetailFabGroup
        canAddOrder={group.isVerified && !ordersUnavailable}
        onAddOrder={() => setAddOrderOpen(true)}
        whatsappGroupInviteUrl={group.whatsappGroupId}
        hidden={mobileSearchOpen}
      />

      <ContactCsFloatingButton className="hidden lg:inline-flex" />
    </div>
  )
}

function UnverifiedBanner({ name }: { name: string }) {
  return (
    <div
      role="status"
      className="rounded-card text-brand-700 bg-brand-50 ring-brand-200 flex items-start gap-3 px-4 py-3 text-sm ring-1"
    >
      <AlertCircleIcon className="mt-0.5 size-5 shrink-0" />
      <p>
        Pesanan di “{name}” belum bisa diambil dari server sampai grupnya terverifikasi. Daftar
        pesanan kosong untuk sementara.
      </p>
    </div>
  )
}

function GroupUnavailable({ error }: { error: Error }) {
  return (
    <div className="grid min-h-dvh place-items-center bg-slate-50 px-6">
      <div className="rounded-card flex max-w-sm flex-col items-center gap-3 bg-white px-6 py-10 text-center ring-1 ring-slate-200">
        <h1 className="text-lg font-bold">Grup tidak bisa dibuka</h1>
        <p className="text-muted text-sm">{error.message}</p>
        <Link
          to="/groups"
          className="text-brand-600 ring-brand-600/40 hover:bg-brand-50 mt-1 inline-flex h-10 items-center gap-2 rounded-xl px-4 text-sm font-semibold ring-1"
        >
          <ArrowLeftIcon className="size-4" />
          Kembali ke daftar grup
        </Link>
      </div>
    </div>
  )
}
